import { useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/components/ui';

/*
  FloatingSearchDock — expanding pill (v3 паттерн).

  Sprint 1.11 fix: boxShadow ВЫНЕСЕН из inline-style в Tailwind classes
  (правило 15 motion principles). Inline-style имеет высшую CSS-специфичность
  и перекрывал hover:shadow-[...]. Из-за этого coral edge glow на hover
  НЕ применялся вообще.

  Теперь:
  - Default shadow через className
  - hover:shadow-[...] перекрывает default (одинаковая специфичность, последний wins)
  - Coral ring + heavier drop shadow на hover работают как задумано
*/

/** На Mac — ⌘K, иначе Ctrl K: подсказка совпадает с тем, что нажимают. */
const SHORTCUT =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K';

interface FloatingSearchDockProps {
  expanded: boolean;
  onOpen: () => void;
  onClose: () => void;
}

export function FloatingSearchDock({ expanded, onOpen, onClose }: FloatingSearchDockProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (expanded) {
      const t = window.setTimeout(() => inputRef.current?.focus(), 280);
      return () => window.clearTimeout(t);
    }
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;
    const handler = (e: MouseEvent) => {
      const dock = document.getElementById('search-dock');
      if (dock && !dock.contains(e.target as Node)) {
        onClose();
      }
    };
    const t = window.setTimeout(() => document.addEventListener('click', handler), 50);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('click', handler);
    };
  }, [expanded, onClose]);

  return (
    <div
      id="search-dock"
      role="dialog"
      aria-label="Глобальный поиск"
      aria-expanded={expanded}
      onClick={() => !expanded && onOpen()}
      className={cn(
        // bottom-10 — pill не пересекает canvas border
        'fixed bottom-10 left-1/2 z-dock -translate-x-1/2',
        'overflow-hidden border glass-frosted text-sm text-fg',
        expanded
          ? // Expanded state
            'h-search-expanded w-search-expanded cursor-auto ' + 'shadow-search-expanded'
          : // Collapsed pill с hover: scale 1.03 (bouncy) + coral ring 0.35 + heavier shadow
            'h-search-bar w-search-bar cursor-pointer ' +
              'shadow-search-bar ' +
              'hover:scale-103 ' +
              'hover:shadow-search-hover',
      )}
      style={{
        borderRadius: expanded ? '20px' : '26px',
        // Per-property transitions. КРИТИЧНО: Tailwind v4 использует
        // ИНДИВИДУАЛЬНЫЕ transform properties (`scale`, `translate`, `rotate`)
        // вместо `transform`. Транзишн на `transform` НЕ анимирует
        // изменения scale/translate utility-классов. Нужно явно транзишнить
        // scale и translate (Sprint 1.13 fix).
        transition:
          'width 480ms cubic-bezier(0.16, 1, 0.3, 1), ' +
          'height 480ms cubic-bezier(0.16, 1, 0.3, 1), ' +
          'border-radius 480ms cubic-bezier(0.16, 1, 0.3, 1), ' +
          'scale 240ms cubic-bezier(0.34, 1.56, 0.64, 1), ' +
          'translate 240ms cubic-bezier(0.34, 1.56, 0.64, 1), ' +
          'transform 240ms cubic-bezier(0.34, 1.56, 0.64, 1), ' +
          'box-shadow 200ms cubic-bezier(0, 0, 0.2, 1)',
      }}
    >
      {/* Top row */}
      <div className="flex h-search-bar shrink-0 items-center gap-3 px-5">
        <Search
          size={18}
          className={cn(
            'shrink-0 transition-colors duration-normal',
            expanded ? 'text-brand-accent' : 'text-fg-subtle',
          )}
        />
        <input
          ref={inputRef}
          type="text"
          data-slot="bare-input"
          readOnly={!expanded}
          placeholder={expanded ? 'Что найти? — модули, люди, инциденты...' : 'Поиск по платформе'}
          onClick={(e) => {
            if (!expanded) {
              e.stopPropagation();
              onOpen();
            }
          }}
          className={cn(
            'min-w-0 flex-1 border-0 bg-transparent outline-none placeholder:text-fg-subtle',
            expanded ? 'cursor-text text-search-input' : 'cursor-pointer text-sm',
          )}
        />
        {expanded ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            aria-label="Закрыть"
            className="shrink-0 rounded-md p-1 text-fg-subtle transition-colors hover:bg-active/50 hover:text-fg dark:hover:bg-white/10"
          >
            <X size={16} />
          </button>
        ) : (
          <kbd className="rounded-md border border-line bg-muted/50 px-2 py-0.5 font-mono text-2xs font-medium text-fg-subtle dark:bg-white/5">
            {SHORTCUT}
          </kbd>
        )}
      </div>

      {/* Results section */}
      <div className="border-t border-line px-5 py-4">
        <p className="text-2xs font-semibold tracking-wider text-fg-subtle uppercase">Скоро</p>
        <p className="mt-2 text-sm leading-relaxed text-fg-muted">
          Глобальный поиск подключим когда появятся первые модули с индексируемым контентом (сервисы,
          Marketplace-модули, Wiki-документы). Пока — заготовка.
        </p>
        <p className="mt-4 text-xs text-fg-subtle">{SHORTCUT} — открыть и закрыть, Esc — закрыть</p>
      </div>
    </div>
  );
}
